import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal, Button, Form } from 'react-bootstrap';
import { fetchFornecedores, fetchCategorias, fetchInformacoesLojaFisica, fetchDateRangeLojaFisica, fetchDatesWithSalesLojaFisica } from '../../services/api';
import DatePicker from '../DatePicker/DatePicker';
import styles from './Filtros.module.css';

const Filtros = ({ onFilterChange, onSearch, onExportCSV, initialFornecedor = '', initialCategoria = '', initialLoja = '', initialInformacao = '', initialOrdenacao = '', initialDataInicio = '', initialDataFim = '', initialSearch = '', initialMediaDia = '', initialCodigosLote = '' }) => {
  // Suporta string legada ou array de fornecedores
  console.log('Filtros V3 Multi-Select Loaded');
  const [fornecedoresSelecionados, setFornecedoresSelecionados] = useState(
    initialFornecedor ? (Array.isArray(initialFornecedor) ? initialFornecedor : [initialFornecedor]) : []
  );
  const [fornecedorDropdownOpen, setFornecedorDropdownOpen] = useState(false);
  const [fornecedorBusca, setFornecedorBusca] = useState('');
  const fornecedorSearchRef = useRef(null);
  const fornecedorDropdownRef = useRef(null);
  const [categoriaSelecionada, setCategoriaSelecionada] = useState(initialCategoria);
  const [lojaSelecionada, setLojaSelecionada] = useState(initialLoja);
  const [informacaoSelecionada, setInformacaoSelecionada] = useState(initialInformacao);
  const [ordenacaoSelecionada, setOrdenacaoSelecionada] = useState(initialOrdenacao);
  const [dataInicio, setDataInicio] = useState(initialDataInicio);
  const [dataFim, setDataFim] = useState(initialDataFim);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [mediaDia, setMediaDia] = useState(initialMediaDia);
  const [dateRange, setDateRange] = useState({ min: '', max: '' });
  const [showLoteModal, setShowLoteModal] = useState(false);
  const [codigosLote, setCodigosLote] = useState('');
  const [loteAtivo, setLoteAtivo] = useState(false);
  const [datasComVendas, setDatasComVendas] = useState([]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fornecedorDropdownRef.current && !fornecedorDropdownRef.current.contains(event.target)) {
        setFornecedorDropdownOpen(false);
        setFornecedorBusca('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focar no campo de busca ao abrir o dropdown
  useEffect(() => {
    if (fornecedorDropdownOpen && fornecedorSearchRef.current) {
      setTimeout(() => fornecedorSearchRef.current?.focus(), 50);
    }
  }, [fornecedorDropdownOpen]);

  // Sincronizar estados internos quando as props iniciais mudarem (para restaurar filtros salvos)
  useEffect(() => {
    // Suportar tanto string quanto array
    if (initialFornecedor) {
      if (Array.isArray(initialFornecedor)) {
        setFornecedoresSelecionados(initialFornecedor);
      } else if (typeof initialFornecedor === 'string') {
        setFornecedoresSelecionados(initialFornecedor.includes(',') ? initialFornecedor.split(',') : [initialFornecedor]);
      } else {
        setFornecedoresSelecionados([initialFornecedor]);
      }
    } else {
      setFornecedoresSelecionados([]);
    }
  }, [initialFornecedor]);

  useEffect(() => {
    setCategoriaSelecionada(initialCategoria);
  }, [initialCategoria]);

  useEffect(() => {
    setLojaSelecionada(initialLoja);
  }, [initialLoja]);

  useEffect(() => {
    setInformacaoSelecionada(initialInformacao);
  }, [initialInformacao]);

  useEffect(() => {
    setOrdenacaoSelecionada(initialOrdenacao);
  }, [initialOrdenacao]);

  useEffect(() => {
    setDataInicio(initialDataInicio);
  }, [initialDataInicio]);

  useEffect(() => {
    setDataFim(initialDataFim);
  }, [initialDataFim]);

  useEffect(() => {
    setSearchTerm(initialSearch);
  }, [initialSearch]);

  useEffect(() => {
    setMediaDia(initialMediaDia);
  }, [initialMediaDia]);

  // Sincroniza o campo de Lote quando aplicado de FORA (ex.: "Filtrar no catálogo" das Compras Automáticas)
  useEffect(() => {
    setCodigosLote(initialCodigosLote || '');
    setLoteAtivo(!!(initialCodigosLote && initialCodigosLote.trim()));
  }, [initialCodigosLote]);

  // Buscar range de datas disponíveis
  useEffect(() => {
    const loadDateRange = async () => {
      const result = await fetchDateRangeLojaFisica();
      if (result.success) {
        setDateRange({
          min: result.data.dataMinima,
          max: result.data.dataMaxima
        });
      }
    };
    loadDateRange();
  }, []);

  // Buscar datas com vendas (para desabilitar dias sem vendas no DatePicker)
  useEffect(() => {
    const loadDatasComVendas = async () => {
      const result = await fetchDatesWithSalesLojaFisica();
      if (result.success && result.data) {
        setDatasComVendas(result.data);
      }
    };
    loadDatasComVendas();
  }, []);

  // Debounce para a busca
  useEffect(() => {
    const timer = setTimeout(() => {
      if (onSearch) onSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, onSearch]);

  // Buscar fornecedores
  const { data: fornecedoresData } = useQuery({
    queryKey: ['fornecedores'],
    queryFn: fetchFornecedores,
    staleTime: 1000 * 60 * 60 // 1 hora
  });

  // Buscar categorias
  const { data: categoriasData } = useQuery({
    queryKey: ['categorias'],
    queryFn: fetchCategorias,
    staleTime: 1000 * 60 * 60 // 1 hora
  });

  // Buscar informações
  const { data: informacoesData } = useQuery({
    queryKey: ['informacoes-loja-fisica'],
    queryFn: fetchInformacoesLojaFisica,
    staleTime: 1000 * 60 * 60 // 1 hora
  });

  const fornecedores = fornecedoresData?.data || [];
  const categorias = categoriasData?.data || [];

  // Informações: Combinar dados da API com opções padrão
  const informacoesApi = informacoesData?.data || [];
  const informacoesPadrao = ['Recorrente', 'Não Comprar', 'Duplicado'];
  const informacoesRaw = [...new Set([...informacoesPadrao, ...informacoesApi])];

  // Mapear "Retirar do Site" para "Retirada da Loja" na Loja Física
  const informacoes = informacoesRaw.map(info =>
    info === 'Retirar do Site' ? 'Retirada da Loja' : info
  );

  const emitFilterChange = (updates = {}) => {
    // Converter array de fornecedores em string separada por vírgula para o backend
    const fornecedorValue = updates.fornecedor ?? fornecedoresSelecionados;
    const fornecedorString = Array.isArray(fornecedorValue) ? fornecedorValue.join(',') : fornecedorValue;

    onFilterChange({
      fornecedor: fornecedorString,
      categoria: updates.categoria ?? categoriaSelecionada,
      loja: updates.loja ?? lojaSelecionada,
      informacao: updates.informacao ?? informacaoSelecionada,
      ordenacao: updates.ordenacao ?? ordenacaoSelecionada,
      dataInicio: updates.dataInicio ?? dataInicio,
      dataFim: updates.dataFim ?? dataFim,
      mediaDia: updates.mediaDia ?? mediaDia
    });
  };

  const handleMediaDiaChange = (e) => {
    const valor = (e.target.value || '').replace(/[^0-9]/g, '');
    setMediaDia(valor);
    emitFilterChange({ mediaDia: valor });
  };

  // Handler para checkbox de fornecedor
  const handleFornecedorToggle = (fornecedor) => {
    setFornecedoresSelecionados(prev => {
      const newSelection = prev.includes(fornecedor)
        ? prev.filter(f => f !== fornecedor)
        : [...prev, fornecedor];

      // Emitir mudança após atualizar estado
      setTimeout(() => emitFilterChange({ fornecedor: newSelection }), 0);
      return newSelection;
    });
  };

  // Limpar todos os fornecedores selecionados
  const handleLimparFornecedores = () => {
    setFornecedoresSelecionados([]);
    emitFilterChange({ fornecedor: [] });
  };

  // Selecionar todos os fornecedores
  const handleSelecionarTodos = () => {
    setFornecedoresSelecionados([...fornecedores]);
    emitFilterChange({ fornecedor: fornecedores });
  };

  const handleCategoriaChange = (e) => {
    const valor = e.target.value;
    setCategoriaSelecionada(valor);
    emitFilterChange({ categoria: valor });
  };

  const handleLojaChange = (e) => {
    const valor = e.target.value;
    setLojaSelecionada(valor);
    emitFilterChange({ loja: valor });
  };

  const handleInformacaoChange = (e) => {
    const valor = e.target.value;
    setInformacaoSelecionada(valor);
    emitFilterChange({ informacao: valor });
  };

  const handleOrdenacaoChange = (e) => {
    const valor = e.target.value;
    setOrdenacaoSelecionada(valor);
    emitFilterChange({ ordenacao: valor });
  };

  const handleDataInicioChange = (valor) => {
    setDataInicio(valor);
    if (dataFim || !valor) {
      emitFilterChange({ dataInicio: valor });
    }
  };

  const handleDataFimChange = (valor) => {
    setDataFim(valor);
    if (dataInicio || !valor) {
      emitFilterChange({ dataFim: valor });
    }
  };

  const handleLimpar = () => {
    setFornecedoresSelecionados([]);
    setCategoriaSelecionada('');
    setLojaSelecionada('');
    setInformacaoSelecionada('');
    setOrdenacaoSelecionada('');
    setDataInicio('');
    setDataFim('');
    setSearchTerm('');
    setMediaDia('');
    if (onSearch) onSearch('');
    onFilterChange({ fornecedor: '', categoria: '', loja: '', informacao: '', ordenacao: '', dataInicio: '', dataFim: '', mediaDia: '' });
  };

  const temFiltrosAtivos = fornecedoresSelecionados.length > 0 || categoriaSelecionada || lojaSelecionada || informacaoSelecionada || ordenacaoSelecionada || dataInicio || dataFim || searchTerm || mediaDia || loteAtivo;

  const handleAplicarLote = () => {
    const codigos = codigosLote
      .split('\n')
      .map(c => c.trim())
      .filter(c => c.length > 0)
      .map(c => {
        // Se for apenas números e tiver menos de 6 dígitos, adiciona zeros à esquerda
        if (/^\d+$/.test(c) && c.length < 6) {
          return c.padStart(6, '0');
        }
        return c;
      });

    if (codigos.length > 0) {
      setLoteAtivo(true);
      const fornecedorString = fornecedoresSelecionados.join(',');
      onFilterChange({
        fornecedor: fornecedorString,
        categoria: categoriaSelecionada,
        loja: lojaSelecionada,
        informacao: informacaoSelecionada,
        ordenacao: ordenacaoSelecionada,
        dataInicio: dataInicio,
        dataFim: dataFim,
        codigosLote: codigos
      });
    }
    setShowLoteModal(false);
  };

  const handleLimparLote = () => {
    setCodigosLote('');
    setLoteAtivo(false);
    const fornecedorString = fornecedoresSelecionados.join(',');
    onFilterChange({
      fornecedor: fornecedorString,
      categoria: categoriaSelecionada,
      loja: lojaSelecionada,
      informacao: informacaoSelecionada,
      ordenacao: ordenacaoSelecionada,
      dataInicio: dataInicio,
      dataFim: dataFim,
      codigosLote: []
    });
    setShowLoteModal(false);
  };

  return (
    <div className={styles.filterBarContainer}>
      <div className={styles.filterGroup}>
        <div className={styles.filterItem} ref={fornecedorDropdownRef} style={{ position: 'relative' }}>
          <label className={styles.filterLabel}>Fornecedor</label>
          <div
            className={`${styles.filterSelect} ${styles.multiSelectTrigger}`}
            onClick={() => setFornecedorDropdownOpen(!fornecedorDropdownOpen)}
            style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {fornecedoresSelecionados.length === 0
                ? 'Todos'
                : fornecedoresSelecionados.length === 1
                  ? fornecedoresSelecionados[0]
                  : `${fornecedoresSelecionados.length} selecionados`}
            </span>
            <span style={{ marginLeft: '8px' }}>{fornecedorDropdownOpen ? '▲' : '▼'}</span>
          </div>
          {fornecedorDropdownOpen && (
            <div className={styles.multiSelectDropdown}>
              <div className={styles.multiSelectSearch}>
                <input
                  ref={fornecedorSearchRef}
                  type="text"
                  className={styles.multiSelectSearchInput}
                  placeholder="Buscar fornecedor..."
                  value={fornecedorBusca}
                  onChange={(e) => setFornecedorBusca(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
                {fornecedorBusca && (
                  <button
                    className={styles.multiSelectSearchClear}
                    onClick={(e) => { e.stopPropagation(); setFornecedorBusca(''); }}
                    type="button"
                  >
                    ✕
                  </button>
                )}
              </div>
              <div className={styles.multiSelectActions}>
                <button type="button" onClick={handleSelecionarTodos} className={styles.multiSelectActionBtn}>
                  Selecionar Todos
                </button>
                <button type="button" onClick={handleLimparFornecedores} className={styles.multiSelectActionBtn}>
                  Limpar
                </button>
              </div>
              <div className={styles.multiSelectList}>
                {fornecedores
                  .filter(f => !fornecedorBusca || f.toLowerCase().includes(fornecedorBusca.toLowerCase()))
                  .map((f) => (
                    <label key={f} className={styles.multiSelectItem}>
                      <input
                        type="checkbox"
                        checked={fornecedoresSelecionados.includes(f)}
                        onChange={() => handleFornecedorToggle(f)}
                      />
                      <span>{f}</span>
                    </label>
                  ))}
                {fornecedorBusca && fornecedores.filter(f => f.toLowerCase().includes(fornecedorBusca.toLowerCase())).length === 0 && (
                  <div className={styles.multiSelectEmpty}>Nenhum fornecedor encontrado</div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className={styles.filterItem}>
          <label className={styles.filterLabel}>Categoria</label>
          <select
            value={categoriaSelecionada}
            onChange={handleCategoriaChange}
            className={styles.filterSelect}
          >
            <option value="">Todas</option>
            {categorias.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div className={styles.filterItem}>
          <label className={styles.filterLabel}>Informação</label>
          <select
            value={informacaoSelecionada}
            onChange={handleInformacaoChange}
            className={styles.filterSelect}
          >
            <option value="">Status</option>
            {informacoes.map((i) => (
              <option key={i} value={i}>{i}</option>
            ))}
          </select>
        </div>

        <div className={styles.filterItem}>
          <label className={styles.filterLabel}>Ordenação</label>
          <select
            value={ordenacaoSelecionada}
            onChange={handleOrdenacaoChange}
            className={styles.filterSelect}
          >
            <option value="">Padrão</option>
            <option value="total_vendas">Total Vendas</option>
            <option value="mais_vendidos_mes">Mais Vendidos Mês</option>
            <option value="menor_estoque">Menor Estoque</option>
            <option value="maior_estoque">Maior Estoque</option>
            <option value="maior_lucro">Maior Lucro</option>
            <option value="maior_markup">Maior Markup</option>
            <option value="menor_markup">Menor Markup</option>
            <option value="maior_media_dia">Maior Média Dia</option>
            <option value="menor_media_dia">Menor Média Dia</option>
          </select>
        </div>

        <div className={styles.filterItem}>
          <label className={styles.filterLabel}>Período De</label>
          <DatePicker
            value={dataInicio}
            onChange={handleDataInicioChange}
            placeholder="Data início"
            availableDates={datasComVendas}
          />
        </div>

        <div className={styles.filterItem}>
          <label className={styles.filterLabel}>Até</label>
          <DatePicker
            value={dataFim}
            onChange={handleDataFimChange}
            placeholder="Data fim"
            availableDates={datasComVendas}
          />
        </div>

        <div className={styles.filterItem} style={{ minWidth: '110px' }}>
          <label className={styles.filterLabel}>Média Dia</label>
          <input
            type="number"
            min="0"
            step="1"
            inputMode="numeric"
            value={mediaDia}
            onChange={handleMediaDiaChange}
            className={styles.filterInput}
            placeholder="ex: 3"
            title="Filtra produtos com média diária de N até N,9 (ex.: 3 → 3,0 a 3,9)"
          />
        </div>

        <div className={styles.filterItem} style={{ minWidth: '200px', flexGrow: 2 }}>
          <label className={styles.filterLabel}>Busca</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.filterInput}
            placeholder="Buscar produtos..."
          />
        </div>
      </div>

      <button
        onClick={() => setShowLoteModal(true)}
        className={`${styles.loteButton} ${loteAtivo ? styles.loteButtonActive : ''}`}
        title="Busca em Lote - colar vários códigos"
      >
        📋 {loteAtivo ? `Lote (${codigosLote.split('\n').filter(c => c.trim()).length})` : 'Lote'}
      </button>

      {onExportCSV && (
        <button
          onClick={onExportCSV}
          className={styles.exportButton}
          title="Exportar produtos listados para CSV"
        >
          📥 CSV
        </button>
      )}

      {temFiltrosAtivos && (
        <button onClick={handleLimpar} className={styles.clearButton} title="Limpar Filtros e Busca">
          <span className={styles.clearIcon}>✕</span>
        </button>
      )}

      {/* Modal de Busca em Lote */}
      <Modal show={showLoteModal} onHide={() => setShowLoteModal(false)} centered>
        <Modal.Header closeButton style={{ background: '#1e3a5f', color: 'white' }}>
          <Modal.Title>📋 Busca em Lote</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Cole ou digite os códigos dos produtos (um por linha):</Form.Label>
            <Form.Control
              as="textarea"
              rows={10}
              value={codigosLote}
              onChange={(e) => setCodigosLote(e.target.value)}
              placeholder="Exemplo:\n001234\n005678\n009012"
              style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
            />
          </Form.Group>
          <div className="mt-2 text-muted" style={{ fontSize: '0.85rem' }}>
            {codigosLote.split('\n').filter(c => c.trim()).length} código(s) detectado(s)
          </div>
        </Modal.Body>
        <Modal.Footer>
          {loteAtivo && (
            <Button variant="outline-danger" onClick={handleLimparLote}>
              Limpar Lote
            </Button>
          )}
          <Button variant="secondary" onClick={() => setShowLoteModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleAplicarLote}>
            Aplicar Filtro
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Filtros;
